import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import fastdom from 'fastdom';
import mediator from './lib/mediator';
import { isBreakpoint } from './lib/detect';
import { scrollTo } from './lib/scroller';
import debounce from 'lodash.debounce';
import zip from 'lodash.zip';
import { Clues } from './clues';
import { HiddenInput } from './hidden-input';
import { Grid } from './grid';
import Stopwatch from '../../Stopwatch';
import {
  buildClueMap,
  buildGrid,
  otherDirection,
  entryHasCell,
  cluesFor,
  mapGrid,
  isCompletedEntry,
  getLastCellInClue,
  getPreviousClueInGroup,
  isFirstCellInClue,
  getNextClueInGroup,
  isLastCellInClue,
  gridSize,
  checkClueHasBeenAnswered,
  buildSeparatorMap,
  cellsForEntry,
} from './helpers';
import { keycodes } from './keycodes';
import { constants } from './constants';
import { saveGridState, loadGridState } from './persistence';
import { classNames } from './classNames';
import { TURBO_STREAM_TYPE, renderTurboStream } from '../../../utils/turbo_stream';

class Crossword extends Component {
  constructor(props) {
    super(props);
    this.state = {
      state: props.state,
      loading: false,
      completed: props.completed,
      grid: this.setGrid(),
      cellInFocus: null,
      directionOfEntry: null
    };
  }

  componentDidMount() {
    mediator.on(
      'window:resize',
      debounce(this.setGridHeight.bind(this), 200),
    );
    mediator.on(
      'window:orientationchange',
      debounce(this.setGridHeight.bind(this), 200),
    );
    this.setGridHeight();

    const entryId = window.location.hash.replace('#', '');
    if (entryId)
      this.focusFirstCellInClueById(entryId);
    else if (this.props.data && this.props.data.entries && this.props.data.entries[0])
      this.focusFirstCellInClueById(this.props.data.entries[0].id);
  }

  componentDidUpdate(prevProps) {
    if (JSON.stringify(prevProps.data.entries) !== JSON.stringify(this.props.data.entries)) { // Grid modified
      this.setState({
        grid: this.setGrid(),
        cellInFocus: null,
        directionOfEntry: null
      }, this.focusCurrentCell)
    } else {
      this.focusCurrentCell();
    }
  }

  setGrid() {
    const dimensions = this.props.data.dimensions;

    this.columns = dimensions.cols;
    this.rows = dimensions.rows;
    this.clueMap = buildClueMap(this.props.data.entries);

    return buildGrid(
      dimensions.rows,
      dimensions.cols,
      this.props.data.entries,
      this.props.value || this.props.loadGrid(this.props.id),
      this.clueMap
    )
  }

  onKeyDown(event) {
    const cell = this.state.cellInFocus;

    if (!event.metaKey && !event.ctrlKey && !event.altKey) {
      if (
        event.keyCode === keycodes.backspace
                || event.keyCode === keycodes.delete
      ) {
        event.preventDefault();
        if (cell) {
          if (this.cellIsEmpty(cell.x, cell.y) || this.state.grid[cell.x][cell.y].isCorrect) {
            this.focusPrevious();
          } else {
            this.setCellValue(cell.x, cell.y, '');
            this.saveGrid();
          }
        }
      } else if (event.keyCode === keycodes.left) {
        event.preventDefault();
        this.moveFocus(-1, 0);
      } else if (event.keyCode === keycodes.up) {
        event.preventDefault();
        this.moveFocus(0, -1);
      } else if (event.keyCode === keycodes.right) {
        event.preventDefault();
        this.moveFocus(1, 0);
      } else if (event.keyCode === keycodes.down) {
        event.preventDefault();
        this.moveFocus(0, 1);
      }
    }
  }

  // called when cell is selected (by click or programtically focussed)
  onSelect(x, y) {
    const cellInFocus = this.state.cellInFocus;
    const clue = cluesFor(this.clueMap, x, y);
    const focussedClue = this.clueInFocus();
    let newDirection;

    const isInsideFocussedClue = () => (focussedClue ? entryHasCell(focussedClue, x, y) : false);

    if (
      cellInFocus
            && cellInFocus.x === x
            && cellInFocus.y === y
            && this.state.directionOfEntry
    ) {
      /** User has clicked again on the highlighted cell, meaning we ought to swap direction */
      newDirection = otherDirection(this.state.directionOfEntry);

      if (clue[newDirection]) {
        this.focusClue(x, y, newDirection);
      }
    } else if (isInsideFocussedClue() && this.state.directionOfEntry) {
      /**
             * If we've clicked inside the currently highlighted clue, then we ought to just shift the cursor
             * to the new cell, not change direction or anything funny.
             */

      this.focusClue(x, y, this.state.directionOfEntry);
    } else {
      this.state.cellInFocus = {
        x,
        y,
      };

      const isStartOfClue = sourceClue => !!sourceClue
                && sourceClue.position.x === x
                && sourceClue.position.y === y;

      /**
             * If the user clicks on the start of a down clue midway through an across clue, we should
             * prefer to highlight the down clue.
             */
      if (!isStartOfClue(clue.across) && isStartOfClue(clue.down)) {
        newDirection = 'down';
      } else if (clue.across) {
        /** Across is the default focus otherwise */
        newDirection = 'across';
      } else {
        newDirection = 'down';
      }
      this.focusClue(x, y, newDirection);
    }
  }

  // Asked here: the data-confirm this button carried was read by rails-ujs on
  // the document, after React's root had already revealed the grid.
  onSolutionClick() {
    if (window.confirm('Êtes-vous sur de vouloir révéler la grille ?')) this.onSolution();
  }

  onSolution() {
    this.props.data.entries.forEach(clue => this.cheat(clue));
    this.saveGrid(true);
  }

  onClickHiddenInput(e) {
    e.stopPropagation();

    const focussed = this.state.cellInFocus;

    if (focussed) {
      this.onSelect(focussed.x, focussed.y);
    }

    /* We need to handle touch seperately as touching an input on iPhone does not fire the
         click event - listen for a touchStart and preventDefault to avoid calling onSelect twice on
         devices that fire click AND touch events. The click event doesn't fire only when the input is already focused */
    if (e.type === 'touchstart') {
      e.preventDefault();
    }
  }

  setGridHeight() {
    if (!this.$gridWrapper) {
      this.$gridWrapper = findDOMNode(this.gridWrapper);
    }

    if (
      isBreakpoint({
        max: 'tablet',
      })
    ) {
      fastdom.read(() => {
        // Our grid is a square, set the height of the grid wrapper
        // to the width of the grid wrapper
        fastdom.write(() => {
          this.$gridWrapper.css(
            'height',
            `${this.$gridWrapper.offset().width}px`,
          );
        });
        this.gridHeightIsSet = true;
      });
    } else if (this.gridHeightIsSet) {
      // Remove inline style if tablet and wider
      this.$gridWrapper.attr('style', '');
    }
  }

  setCellValue(x, y, value, triggerOnMoveCallback = true) {
    this.setState({
      grid: mapGrid(this.state.grid, (cell, gridX, gridY) => {
        if (gridX === x && gridY === y) {
          const previousValue = cell.value;
          if (!cell.isCorrect) { // Update cell value on if not already corrected
            cell.value = value;
            cell.isError = false;
          }
          if (triggerOnMoveCallback) {
            this.props.onMove({
              x, y, value, previousValue,
            });
          }
        }

        return cell;
      }),
    });
  }

  getCellValue(x, y) {
    return this.state.grid[x][y].value;
  }

  setReturnPosition(position) {
    this.returnPosition = position;
  }

  updateGrid(gridState) {
    this.setState({
      grid: buildGrid(
        this.rows,
        this.columns,
        this.props.data.entries,
        gridState,
        this.clueMap
      ),
    });
  }

  insertCharacter(character) {
    if (this.state.loading) return;

    const normalizedCharacter = character.toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const cell = this.state.cellInFocus;
    if (
      /[A-Za-zÀ-ÿ0-9]/.test(normalizedCharacter)
            && normalizedCharacter.length === 1
            && cell
    ) {
      this.setCellValue(cell.x, cell.y, normalizedCharacter);
      this.checkAll();
    }
  }

  cellIsEmpty(x, y) {
    return !this.getCellValue(x, y);
  }

  goToReturnPosition() {
    if (
      isBreakpoint({
        max: 'mobile',
      })
    ) {
      if (this.returnPosition) {
        scrollTo(this.returnPosition, 250, 'easeOutQuad');
      }
      this.returnPosition = null;
    }
  }

  indexOfClueInFocus() {
    return this.props.data.entries.indexOf(this.clueInFocus());
  }

  focusPreviousClue() {
    const i = this.indexOfClueInFocus();
    const entries = this.props.data.entries;

    if (i !== -1) {
      const newClue = entries[i === 0 ? entries.length - 1 : i - 1];
      this.focusClue(
        newClue.position.x,
        newClue.position.y,
        newClue.direction,
      );
    }
  }

  focusNextClue() {
    const i = this.indexOfClueInFocus();
    const entries = this.props.data.entries;

    if (i !== -1 && i !== entries.length - 1) {
      const newClue = entries[i + 1];
      this.focusClue(
        newClue.position.x,
        newClue.position.y,
        newClue.direction,
      );
    } else {
      this.blurClue();
    }
  }

  moveFocus(deltaX, deltaY) {
    const cell = this.state.cellInFocus;

    if (!cell) {
      return;
    }

    const x = cell.x + deltaX;
    const y = cell.y + deltaY;
    let direction = 'down';

    if (
      this.state.grid[x]
            && this.state.grid[x][y]
            && this.state.grid[x][y].isEditable
    ) {
      if (deltaY !== 0) {
        direction = 'down';
      } else if (deltaX !== 0) {
        direction = 'across';
      }
      this.focusClue(x, y, direction);
    }
  }

  isAcross() {
    return this.state.directionOfEntry === 'across';
  }

  focusPrevious() {
    const cell = this.state.cellInFocus;
    const clue = this.clueInFocus();

    if (cell && clue) {
      if (isFirstCellInClue(cell, clue)) {
        const newClue = getPreviousClueInGroup(
          this.props.data.entries,
          clue,
        );
        if (newClue) {
          const newCell = getLastCellInClue(newClue);
          this.focusClue(newCell.x, newCell.y, newClue.direction);
        }
      } else if (this.isAcross()) {
        this.moveFocus(-1, 0);
      } else {
        this.moveFocus(0, -1);
      }
    }
  }

  focusNext() {
    const cell = this.state.cellInFocus;
    const clue = this.clueInFocus();

    if (cell && clue) {
      if (isLastCellInClue(cell, clue)) {
        const newClue = getNextClueInGroup(
          this.props.data.entries,
          clue,
        );
        if (newClue) {
          this.focusClue(
            newClue.position.x,
            newClue.position.y,
            newClue.direction,
          );
        }
      } else if (this.isAcross()) {
        this.moveFocus(1, 0);
      } else {
        this.moveFocus(0, 1);
      }
    }
  }

  inputPosition(x, y) {
    const borderWidth = this.borderWidth();
    const inputWidth = this.inputWidth();

    return {
      x: `${inputWidth * x + borderWidth * (x + 1) - this.inputPositionOffsetCorrection(x)}px`,
      y: `${inputWidth * y + borderWidth * (y + 1) - this.inputPositionOffsetCorrection(y)}px`
    };
  }

  inputPositionOffsetCorrection = (position) => this.gridWidth() * 0.0005 * position;

  gridWidth = () => this.gridWrapper.offsetWidth || gridSize(this.columns);

  borderWidth = () => constants.borderSize * this.cellWidth() / constants.cellSize;

  cellWidth = () => this.gridWidth() / this.columns;

  inputWidth = () => this.cellWidth() - this.borderWidth();

  inputSize = () => `${this.inputWidth()}px`;

  focusHiddenInput(x, y) {
    const wrapper = findDOMNode(this.hiddenInputComponent.wrapper);
    const position = this.inputPosition(x, y);
    const inputSize = this.inputSize();

    /** Redefine input width / height */
    wrapper.style.width = inputSize;
    wrapper.style.height = inputSize;

    /** This has to be done before focus to move viewport accordingly */
    wrapper.style.left = position.x;
    wrapper.style.top = position.y;

    const hiddenInputNode = findDOMNode(this.hiddenInputComponent.input);

    if (document.activeElement !== hiddenInputNode) {
      hiddenInputNode.focus();
    }
  }

  // Focus corresponding clue for a given cell
  focusClue(x, y, direction) {
    const clues = cluesFor(this.clueMap, x, y);
    const clue = clues[direction];

    if (clues && clue) {
      this.focusHiddenInput(x, y);

      this.setState({
        grid: this.state.grid,
        cellInFocus: {
          x,
          y,
        },
        directionOfEntry: direction,
      });

      // Side effect
      window.history.replaceState(
        undefined,
        document.title,
        `#${clue.id}`,
      );
    }
  }

  blurClue() {
    this.setState({
      cellInFocus: null,
      directionOfEntry: null
    });

    this.hiddenInputComponent.input.blur();

    // Reset hidden input position
    const wrapper = findDOMNode(this.hiddenInputComponent.wrapper);

    wrapper.style.width = null;
    wrapper.style.height = null;
    wrapper.style.left = null;
    wrapper.style.top = null;

    // Reset url
    window.history.replaceState(
      undefined,
      document.title,
      window.location.origin + window.location.pathname + window.location.search
    );
  }

  // Focus first cell in given clue
  focusFirstCellInClue(entry) {
    this.focusClue(entry.position.x, entry.position.y, entry.direction);
  }

  focusFirstCellInClueById(clueId) {
    const newEntry = this.props.data.entries.find(val => val.id === clueId);
    if (newEntry) {
      this.focusFirstCellInClue(newEntry);
    }
  }

  focusCurrentCell() {
    if (this.state.cellInFocus) {
      this.focusHiddenInput(
        this.state.cellInFocus.x,
        this.state.cellInFocus.y,
      );
    }
  }

  clueInFocus() {
    if (this.state.cellInFocus) {
      const cluesForCell = cluesFor(
        this.clueMap,
        this.state.cellInFocus.x,
        this.state.cellInFocus.y,
      );

      if (this.state.directionOfEntry) {
        return cluesForCell[this.state.directionOfEntry];
      }
    }

    return null;
  }

  allHighlightedClues() {
    return this.props.data.entries.filter(clue => this.clueIsInFocusGroup(clue));
  }

  clueIsInFocusGroup(clue) {
    if (this.state.cellInFocus) {
      const cluesForCell = cluesFor(
        this.clueMap,
        this.state.cellInFocus.x,
        this.state.cellInFocus.y,
      );

      if (
        this.state.directionOfEntry
                && cluesForCell[this.state.directionOfEntry]
      ) {
        return cluesForCell[this.state.directionOfEntry].group.includes(
          clue.id,
        );
      }
    }
    return false;
  }

  cluesData() {
    return this.props.data.entries.map((entry) => {
      const hasAnswered = checkClueHasBeenAnswered(
        this.state.grid,
        entry,
      );
      const isCompleted = isCompletedEntry(this.state.grid, entry);
      const isErroned = hasAnswered && !isCompleted;
      return {
        entry,
        hasAnswered,
        isCompleted,
        isErroned,
        isSelected: this.clueIsInFocusGroup(entry),
      };
    });
  }

  cheat(entry) {
    const cells = cellsForEntry(entry);

    if (entry.solution) {
      this.setState({
        grid: mapGrid(this.state.grid, (cell, x, y) => {
          if (cells.some(c => c.x === x && c.y === y)) {
            const n = entry.direction === 'across'
              ? x - entry.position.x
              : y - entry.position.y;
            const previousValue = cell.value;
            cell.value = entry.solution[n];
            this.props.onMove({
              x, y, value: cell.value, previousValue,
            });
          }

          return cell;
        }),
      });
    }
  }

  checkAll() {
    const promises = this.props.data.entries.map(entry => {
      return new Promise((resolve) => {
        const notAlreadyCompleted = this.state.completed.indexOf(entry.id) === -1;
        const isCompleted = isCompletedEntry(this.state.grid, entry);

        if (notAlreadyCompleted && isCompleted) {
          const cells = cellsForEntry(entry);
          const goodCells = zip(cells, entry.solution.split('')).map(cellAndSolution => cellAndSolution[0]);

          goodCells.forEach((goodCell, index) => {
            const ended = goodCells.length === index + 1;

            setTimeout(() => {
              this.setState(prevState => ({
                loading: true,
                completed: ended ? prevState.completed.concat(entry.id) : prevState.completed,
                grid: mapGrid(prevState.grid, (cell, gridX, gridY) => {
                  if (
                    goodCell.x === gridX && goodCell.y === gridY
                  ) {
                    cell.isCorrect = true;
                  }

                  return cell;
                }),
              }), () => ended ? resolve(entry.solution) : {});
            }, index * 200);
          });

        } else resolve();
      });
    });

    Promise.all(promises).then((values) => {
      if (this.save) clearTimeout(this.save);
      this.save = setTimeout(this.saveGrid.bind(this), 500);

      this.setState({
        loading: false
      });

      if (values.filter(item => item).length) { // New value completed, go to the next clue
        this.focusNextClue();
      } else {
        this.focusNext();
      }
    });

  }

  hiddenInputValue() {
    const cell = this.state.cellInFocus;

    let currentValue;

    if (cell) {
      currentValue = this.state.grid[cell.x][cell.y].value;
    }

    return currentValue || '';
  }

  hasSolutions() {
    return 'solution' in this.props.data.entries[0];
  }

  isHighlighted(x, y) {
    const focused = this.clueInFocus();
    return focused
      ? focused.group.some((id) => {
        const entry = this.props.data.entries.find(e => e.id === id);
        return entryHasCell(entry, x, y);
      })
      : false;
  }

  saveGrid(resign = false) {
    const entries = this.state.grid.map(row => row.map(cell => cell.value));
    this.props.saveGrid(this.props.id, entries); // Save in localstorage

    // Persist in database
    const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    if (!this.props.endpoint) return;

    fetch(this.props.endpoint, {
      method: 'PUT',
      body: JSON.stringify({
        resign: resign,
        value: entries,
        completed: this.state.completed
      }),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': token
      },
      credentials: 'same-origin'
    }).then(response => response.json())
      .then(data => {
        this.setState({
          state: data.state
        });

        if (data.state === 'retry') return;

        fetch(this.props.endpoint, {
          headers: {
            'Accept': TURBO_STREAM_TYPE,
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-Token': token
          },
          credentials: 'same-origin'
        }).then(renderTurboStream);
      });
  }

  render() {
    const focused = this.clueInFocus();

    const gridProps = {
      rows: this.rows,
      columns: this.columns,
      cells: this.state.grid,
      separators: buildSeparatorMap(this.props.data.entries),
      crossword: this,
      focussedCell: this.state.cellInFocus,
      ref: (grid) => {
        this.grid = grid;
      },
    };

    try {
      return (
        <div className="crossword h-full">
          <div
            className={`crossword__container md:grid md:grid-cols-5 md:col-span-2 lg:grid-cols-3 md:gap-6 h-full`}
            data-link-name="Crosswords"
          >
            <div className="col-span-3 lg:col-span-2 h-full md:overflow-auto flex items-center" onClick={this.blurClue.bind(this)}>
              <div
                className="crossword__container__game w-full mx-auto px-3 md:px-0"
                ref={(game) => {
                  this.game = game;
                }}
              >
                <div
                  className="crossword__container__grid-wrapper"
                  ref={(gridWrapper) => {
                    this.gridWrapper = gridWrapper;
                  }}
                >
                  <div
                    className="crossword__sticky-clue-wrapper"
                    ref={(stickyClueWrapper) => {
                      this.stickyClueWrapper = stickyClueWrapper;
                    }}
                  >
                    <div
                      className={classNames({
                        'crossword__sticky-clue flex items-center': true,
                        'hidden': !focused,
                      })}
                    >
                      {focused && (
                      <div className="crossword__sticky-clue__inner">
                        <div className="crossword__sticky-clue__inner__inner">
                          <strong>
                            {focused.number}
                            {'. '}
                            <span className="crossword__sticky-clue__direction">
                              {focused.direction == "across" ? "Horizontal" : "Vertical"}
                            </span>
                          </strong>
                          {' : '}
                          {focused.clue}
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                  {Grid(gridProps)}
                  <HiddenInput
                    crossword={this}
                    value={this.hiddenInputValue()}
                    ref={(hiddenInputComponent) => {
                      this.hiddenInputComponent = hiddenInputComponent;
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={`col-span-3 md:col-span-2 lg:col-span-1 bg-black-mid md:h-full md:overflow-y-auto -mt-16 md:mt-0 md:overflow-x-hidden z-20 ${this.props.editable ? 'rounded-lg' : 'md:rounded-t-lg'}`}>
              <div className="pt-5 pb-6">
                <div>
                  <div className="flex justify-between border-b border-grey border-opacity-50 pb-4 mb-6 mx-6">
                    <p className="text-white text-left text-lg font-semibold">Liste des définitions</p>
                    { this.props.start_at && (
                      <div className="text-white">
                        <i className="fas fa-stopwatch mr-1"></i>
                        <span className="js-stopwatch inline-block">
                          <Stopwatch startAt={this.props.start_at} endAt={this.props.end_at} />
                        </span>
                      </div>
                    ) }
                  </div>
                  <Clues
                    clues={this.cluesData()}
                    focussed={focused}
                    focusFirstCellInClueById={this.focusFirstCellInClueById.bind(
                      this,
                    )}
                    setReturnPosition={this.setReturnPosition.bind(this)}
                  />
                  { !this.props.editable && this.state.state === "retry" && (
                    <div className="mx-6 mt-8 text-center">
                      <button onClick={this.onSolutionClick.bind(this)} className="btn inline-block rounded-full transition duration-100 ease-in-out hover:shadow-lg shadow bg-success text-white text-xl px-6 py-3 font-bold w-full text-center">
                        Révéler les solutions
                      </button>
                      <p className="text-sm text-grey italic mt-4 px-8">
                        <i className="fas fa-info-circle text-white text-base inline-block align-middle mb-1 mr-1"></i>
                        En cliquant sur le bouton « Révéler les solutions » vous abandonnerez la grille.
                      </p>
                    </div>
                  ) }
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } catch (error) {
      return null;
    }
  }
}

Crossword.defaultProps = {
  onMove: () => {},
  loadGrid: id => loadGridState(id),
  saveGrid: (id, grid) => saveGridState(id, grid),
  controls: true,
  editable: false,
  completed: []
};

export default Crossword;
